import AuditLog from "@/components/AuditLog";
import DashboardStats from "@/components/DashboardStats";
import DoctorManagement from "@/components/DoctorManagement";
import RequestsList from "@/components/RequestsList";
import DayCalendar from "@/components/DayCalendar";
import AppointmentTable from "@/components/AppointmentTable";
import DoctorToday from "@/components/DoctorToday";
import DoctorProfile from "@/components/DoctorProfile";
import DaySummary from "@/components/DaySummary";
import DoctorReports from "@/components/DoctorReports";
import PatientHistory from "@/components/PatientHistory";
import ClinicSettings from "@/components/ClinicSettings";
import UsersPanel from "@/components/users/UsersPanel";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { can } from "@/lib/roles";

export default function DashboardContent({ active, data, query, setQuery, selectedDate, setSelectedDate, tableProps, fmtDate, actions, role, user, userForm }) {
  const { doctors, appointments, requests, stats, filtered, pendingCount, apptDays, dayAppointments } = data;
  const pending = appointments.filter((a) => a.status === "pending");

  switch (active) {
    case "dashboard":
      return (
        <>
          <DashboardStats stats={stats} pendingCount={pendingCount} doctors={doctors} />
          {pending.length > 0 && (
            <div data-testid="pending-online-section">
              <h2 className="font-head text-lg font-semibold mb-4 flex items-center gap-2">Pending Online Bookings <span className="text-xs font-sans font-semibold bg-amber-100 text-amber-800 rounded-full px-2 py-0.5">{pending.length}</span></h2>
              <AppointmentTable rows={pending} {...tableProps} testid="pending-appointments-table" />
            </div>
          )}
          <div>
            <h2 className="font-head text-lg font-semibold mb-4">Recent Appointments</h2>
            <AppointmentTable rows={appointments.slice(0, 5)} {...tableProps} />
          </div>
        </>
      );
    case "today":
      return <DoctorToday appointments={appointments} doctor={doctors.find((d) => d.id === user?.doctor_id)} onReceipt={tableProps.setReceipt} onClinical={tableProps.openClinical} />;
    case "appointments":
      return (
        <>
          <div className="relative max-w-md">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="appointment-search-input" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Search patient, doctor, disease, receipt…" className="pl-9 bg-white" />
          </div>
          <AppointmentTable rows={filtered} {...tableProps} />
        </>
      );
    case "clinic":
      return <ClinicSettings />;
    case "patients":
      return <PatientHistory fmtDate={fmtDate} onReceipt={tableProps.setReceipt} />;
    case "calendar":
      return <DayCalendar selectedDate={selectedDate} onSelectDate={setSelectedDate} apptDays={apptDays} dayAppointments={dayAppointments} onViewReceipt={tableProps.setReceipt} />;
    case "doctors":
      return <DoctorManagement doctors={doctors} onEdit={actions.editDoctor} onDelete={actions.deleteDoctor} />;
    case "requests":
      return <RequestsList requests={requests} fmtDate={fmtDate} onConvert={actions.convertRequest} onStatus={actions.setReqStatus} onDelete={actions.deleteRequest} canDelete={can(role, "requests:delete")} />;
    case "receipts":
      return (
        <>
          <p className="text-sm text-slate-500">Every visit generates a receipt. Click view to print or download as PDF.</p>
          <AppointmentTable rows={filtered} {...tableProps} receiptsOnly />
        </>
      );
    case "users":
      return <UsersPanel doctors={doctors} formOpen={userForm.open} setFormOpen={userForm.setOpen} />;
    case "summary":
      return <DaySummary />;
    case "reports":
      return <DoctorReports doctors={doctors} role={role} />;
    case "profile":
      return <DoctorProfile onSaved={data.load} />;
    case "activity":
      return (
        <>
          <p className="text-sm text-slate-500">Who created, edited, deleted or printed appointments and receipts — and when.</p>
          <AuditLog />
        </>
      );
    default:
      return null;
  }
}
